/**
 * hooks/use-countdowns.ts
 *
 * TanStack Query hooks for countdowns:
 * - useCountdowns()       — list all countdowns ordered by target_date ASC
 * - useCreateCountdown()  — POST /countdowns (JSON)
 * - useDeleteCountdown()  — DELETE /countdowns/:id
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiSuccess, Countdown, CreateCountdownInput } from '@just-us/shared'
import { apiFetch } from '../lib/api-client'

export const COUNTDOWNS_QUERY_KEY = ['countdowns'] as const

// ---------------------------------------------------------------------------
// useCountdowns
// ---------------------------------------------------------------------------

export const useCountdowns = () =>
  useQuery({
    queryKey: COUNTDOWNS_QUERY_KEY,
    queryFn: () =>
      apiFetch<{ data: Countdown[]; total: number }>('/countdowns'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

// ---------------------------------------------------------------------------
// useCreateCountdown
// ---------------------------------------------------------------------------

export const useCreateCountdown = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateCountdownInput) =>
      apiFetch<ApiSuccess<Countdown>>('/countdowns', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COUNTDOWNS_QUERY_KEY })
    },
  })
}

// ---------------------------------------------------------------------------
// useDeleteCountdown
// ---------------------------------------------------------------------------

export const useDeleteCountdown = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<ApiSuccess<{ deleted: boolean }>>(`/countdowns/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COUNTDOWNS_QUERY_KEY })
    },
  })
}
